import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const Container = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 12px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background 0.2s;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  text-align: center;
  margin-top: 20px;
  color: ${props => props.type === 'error' ? '#dc3545' : '#28a745'};
`;

export default function ProfileSettings() {
  const { currentUser, updateUserProfile, getUserData } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate(); // Get the navigate function

  useEffect(() => {
    const fetchSettings = async () => {
      // Only fetch if user exists, getUserData is available, and not already initialized
      if (currentUser && getUserData && !isInitialized) {
        try {
          const userData = await getUserData(currentUser.uid);
          if (userData) {
            setDisplayName(userData.displayName || currentUser.displayName || '');
            setPhotoURL(userData.photoURL || currentUser.photoURL || '');
            setIsInitialized(true);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Handle error if necessary
        }
      }
    };
    fetchSettings();
  }, [currentUser, getUserData, isInitialized]); // Include all dependencies used inside the effect

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setMessageType('');

    if (!currentUser) {
      setMessage('User not logged in.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      // Update profile information (display name, photo URL)
      await updateUserProfile(currentUser.uid, { displayName, photoURL });

      // You can add logic here to update other user settings stored in Firestore
      // For example:
      // await updateUserSettings({ notifications: true, privacy: 'public' });

      setMessage('Profile updated successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage(`Failed to update profile: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* Add the back button */}
      <Button onClick={() => navigate('/chat')} style={{ marginTop: '20px' }}>
        Back to Chat
      </Button>
      <Title>Profile Settings</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={loading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="photoURL">Photo URL</Label>
          <Input
            id="photoURL"
            type="text"
            value={photoURL}
            onChange={(e) => setPhotoURL(e.target.value)}
            disabled={loading}
          />
        </FormGroup>
        {/* Add more form groups for other settings */}
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Form>
      {message && <Message type={messageType}>{message}</Message>}
    </Container>
  );
}